import * as React from 'react';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import ImageExtension from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import type { SuggestionOptions } from '@tiptap/suggestion';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2,
  List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon, Table as TableIcon,
  Code, Paperclip, Palette, Highlighter, Undo2, Redo2, AtSign,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

// ─── Mention data + pure helpers (kept framework-agnostic and unit-testable,
// matching the previous contentEditable implementation's test contract) ────

export interface MentionAgent {
  id: string;
  name: string;
  email: string;
}

/** The visible label for a mention: display name, falling back to email. */
export function mentionDisplayLabel(agent: MentionAgent): string {
  const trimmedName = agent.name?.trim();
  return trimmedName || agent.email;
}

const TEXT_COLORS = ['#111827', '#DC2626', '#D97706', '#16A34A', '#2563EB', '#7C3AED'];
const HIGHLIGHT_COLORS = ['#FEF08A', '#BBF7D0', '#BFDBFE', '#FBCFE8'];

interface RichTextEditorProps {
  value?: string;
  onChange: (html: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  minHeight?: string;
  disabled?: boolean;
  onAttach?: () => void;
  /** Enable @mention autocomplete for agents */
  mentionsEnabled?: boolean;
}

/**
 * Tiptap-backed mention suggestion list rendered via ReactRenderer — a
 * lightweight dropdown that mirrors the previous contentEditable picker's
 * look, now driven by ProseMirror's suggestion plugin instead of manual
 * Range/Selection bookkeeping.
 */
interface MentionLabels { noAgents: string; suggestions: string }
const DEFAULT_MENTION_LABELS: MentionLabels = { noAgents: 'No agents found', suggestions: 'Mention suggestions' };
function MentionList({
  items,
  command,
  labels = DEFAULT_MENTION_LABELS,
}: {
  items: MentionAgent[];
  command: (attrs: { id: string; label: string }) => void;
  labels?: MentionLabels;
}) {
  const [selected, setSelected] = React.useState(0);
  React.useEffect(() => setSelected(0), [items]);

  if (items.length === 0) {
    return (
      <div className="w-64 rounded-md border border-line bg-white px-3 py-2 text-sm text-ink-subtle shadow-raised">
        {labels.noAgents}
      </div>
    );
  }

  return (
    <div className="max-h-48 w-64 overflow-auto rounded-md border border-line bg-white shadow-raised" role="listbox" aria-label={labels.suggestions}>
      {items.map((agent, index) => (
        <button
          key={agent.id}
          type="button"
          role="option"
          aria-selected={index === selected}
          onMouseDown={(event) => {
            event.preventDefault();
            command({ id: agent.id, label: mentionDisplayLabel(agent) });
          }}
          onMouseEnter={() => setSelected(index)}
          className={cn(
            'flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-surface-hover',
            index === selected && 'bg-brand-50',
          )}
        >
          <span className="font-medium text-ink">{agent.name}</span>
          <span className="text-xs text-ink-subtle">{agent.email}</span>
        </button>
      ))}
    </div>
  );
}

function buildMentionSuggestion(getLabels: () => MentionLabels): Omit<SuggestionOptions, 'editor'> {
  let popupEl: HTMLDivElement | null = null;
  let renderer: ReactRenderer | null = null;
  let fetchTimer: ReturnType<typeof setTimeout> | undefined;

  function positionPopup(rect: { top: number; left: number; bottom: number }) {
    if (!popupEl) return;
    popupEl.style.position = 'fixed';
    popupEl.style.top = `${rect.bottom + 4}px`;
    popupEl.style.left = `${rect.left}px`;
    popupEl.style.zIndex = '9999';
  }

  return {
    items: async ({ query }: { query: string }): Promise<MentionAgent[]> => {
      return new Promise((resolve) => {
        clearTimeout(fetchTimer);
        fetchTimer = setTimeout(async () => {
          try {
            const res = await api.get<{ data: MentionAgent[] }>('/tickets/mentionable-agents', {
              params: { q: query || undefined },
            });
            resolve(Array.isArray(res) ? res : res.data ?? []);
          } catch {
            resolve([]);
          }
        }, 300);
      });
    },
    render: () => ({
      onStart: (props) => {
        popupEl = document.createElement('div');
        document.body.appendChild(popupEl);
        renderer = new ReactRenderer(MentionList, { props: { ...props, labels: getLabels() }, editor: props.editor as Editor });
        popupEl.appendChild(renderer.element);
        if (props.clientRect) positionPopup(props.clientRect() as DOMRect);
      },
      onUpdate: (props) => {
        renderer?.updateProps(props);
        if (props.clientRect) positionPopup(props.clientRect() as DOMRect);
      },
      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          popupEl?.remove();
          popupEl = null;
          return true;
        }
        return false;
      },
      onExit: () => {
        renderer?.destroy();
        popupEl?.remove();
        popupEl = null;
        renderer = null;
      },
    }),
  };
}

interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function ToolbarButton({ icon: Icon, title, active, disabled, onClick }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onMouseDown={(event) => {
        // Keep the editor selection while allowing keyboard activation to use
        // the semantic click handler below.
        event.preventDefault();
      }}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded p-1.5 text-ink-muted hover:bg-line hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50',
        active && 'bg-brand-100 text-brand-700',
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function SwatchPicker({ icon: Icon, title, colors, onPick }: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  colors: string[];
  onPick: (color: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <ToolbarButton icon={Icon} title={title} onClick={() => setOpen((v) => !v)} />
      {open && (
        <div className="absolute left-0 top-full z-dropdown mt-1 flex gap-1 rounded-md border border-line bg-white p-2 shadow-raised">
          {colors.map((color) => (
            <button
              key={color}
              type="button"
              title={color}
              aria-label={color}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                onPick(color);
                setOpen(false);
              }}
              className="h-5 w-5 rounded-full border border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type UrlDialogKind = 'link' | 'image';

function isAllowedEditorUrl(value: string, kind: UrlDialogKind): boolean {
  const normalized = value.trim().toLowerCase();
  if (!normalized || /^(javascript|vbscript|data):/.test(normalized)) return false;
  if (kind === 'image') return normalized.startsWith('https://') || normalized.startsWith('http://') || normalized.startsWith('/') || !normalized.includes(':');
  return normalized.startsWith('https://')
    || normalized.startsWith('http://')
    || normalized.startsWith('mailto:')
    || normalized.startsWith('/')
    || normalized.startsWith('#')
    || !normalized.includes(':');
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  label,
  error,
  minHeight = '160px',
  disabled,
  onAttach,
  mentionsEnabled = false,
}: RichTextEditorProps) {
  const { t } = useI18n();
  const mentionLabelsRef = React.useRef<MentionLabels>(DEFAULT_MENTION_LABELS);
  mentionLabelsRef.current = { noAgents: t('editor.noAgents'), suggestions: t('editor.mentionSuggestions') };
  const mentionSuggestion = React.useMemo(() => buildMentionSuggestion(() => mentionLabelsRef.current), []);

  const [urlDialogKind, setUrlDialogKind] = React.useState<UrlDialogKind | null>(null);
  const [urlValue, setUrlValue] = React.useState('');
  const [urlError, setUrlError] = React.useState<string | null>(null);
  const urlInputRef = React.useRef<HTMLInputElement>(null);

  // ponytail: sync external value into the editor only when the parent
  // resets content (e.g. opening the description drawer with a fresh value).
  // prevValue tracks what onUpdate last emitted so the effect can skip
  // round-trips that originated from the editor itself. suppressSync
  // blocks the effect while programmatic content synchronization is in flight;
  // Tiptap can emit an update for setContent even when the user did not edit.
  const suppressSync = React.useRef(false);
  const prevValue = React.useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] }, link: false, underline: false }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false, autolink: true }),
      ImageExtension,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: placeholder ?? t('editor.defaultPlaceholder') }),
      ...(mentionsEnabled
        ? [Mention.configure({
            HTMLAttributes: { class: 'mention-tag inline-block rounded bg-brand-100 px-1 text-brand-700 text-sm font-medium', 'data-mention-user-id': null },
            renderHTML: ({ node }) => [
              'span',
              {
                class: 'mention-tag inline-block rounded bg-brand-100 px-1 text-brand-700 text-sm font-medium',
                'data-mention-user-id': node.attrs.id,
              },
              `@${node.attrs.label}`,
            ],
            suggestion: { char: '@', ...mentionSuggestion },
          })]
        : []),
    ],
    content: value ? sanitizeHtml(value) : '',
    editable: !disabled,
    onUpdate: ({ editor: current }) => {
      if (suppressSync.current) return;
      const html = sanitizeHtml(current.getHTML());
      prevValue.current = html;
      onChange(html);
    },
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label || t('editor.ariaLabel'),
        class: 'prose prose-sm min-w-0 max-w-none break-words px-3 py-2 outline-none [&>*:first-child]:mt-0 [&_img]:max-w-full [&_table]:max-w-full [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap',
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openUrlDialog(kind: UrlDialogKind) {
    if (!editor) return;
    const currentHref = editor.getAttributes('link').href;
    setUrlDialogKind(kind);
    setUrlValue(kind === 'link' && typeof currentHref === 'string' ? currentHref : '');
    setUrlError(null);
  }

  function closeUrlDialog() {
    setUrlDialogKind(null);
    setUrlValue('');
    setUrlError(null);
  }

  function submitUrl(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editor || !urlDialogKind) return;
    const url = urlValue.trim();
    if (!isAllowedEditorUrl(url, urlDialogKind)) {
      setUrlError(urlDialogKind === 'image'
        ? t('editor.invalidImage')
        : t('editor.invalidLink'));
      return;
    }

    const applied = urlDialogKind === 'link'
      ? editor.chain().focus().setLink({ href: url }).run()
      : editor.chain().focus().setImage({ src: url }).run();
    if (!applied) {
      setUrlError(urlDialogKind === 'link' ? t('editor.selectTextForLink') : t('editor.imageInsertFailed'));
      return;
    }
    closeUrlDialog();
  }

  React.useEffect(() => {
    // Changing editability is programmatic state, not a content edit. Tiptap
    // emits an update by default here, which would falsely mark a template
    // dirty immediately after it loads.
    if (editor) editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  React.useEffect(() => {
    if (!editor) return;
    // Only sync when the parent passes a genuinely new value (not one we
    // just emitted via onChange). Compare against the last prop we saw.
    if (value === prevValue.current) return;
    prevValue.current = value;
    const nextContent = value ? sanitizeHtml(value) : '';
    if (editor.getHTML() !== nextContent) {
      suppressSync.current = true;
      editor.commands.setContent(nextContent);
      suppressSync.current = false;
    }
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div className="w-full">
      {label && <label className="mb-1 block text-sm font-medium text-ink-muted">{label}</label>}
      <div
        className={cn(
          'rounded-md border',
          error ? 'border-danger-500' : 'border-line-strong',
          'focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-1',
        )}
      >
        <div className="flex flex-wrap items-center gap-0.5 border-b bg-surface-hover px-2 py-1" role="toolbar" aria-label={t('editor.toolbarAria')}>
          <ToolbarButton icon={Bold} title={t('editor.bold')} active={editor.isActive('bold')} disabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()} />
          <ToolbarButton icon={Italic} title={t('editor.italic')} active={editor.isActive('italic')} disabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()} />
          <ToolbarButton icon={UnderlineIcon} title={t('editor.underline')} active={editor.isActive('underline')} disabled={disabled} onClick={() => editor.chain().focus().toggleUnderline().run()} />
          <ToolbarButton icon={Strikethrough} title={t('editor.strikethrough')} active={editor.isActive('strike')} disabled={disabled} onClick={() => editor.chain().focus().toggleStrike().run()} />
          <span className="mx-0.5 h-4 w-px bg-line" aria-hidden="true" />
          <ToolbarButton icon={Heading1} title={t('editor.heading1')} active={editor.isActive('heading', { level: 1 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} />
          <ToolbarButton icon={Heading2} title={t('editor.heading2')} active={editor.isActive('heading', { level: 2 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
          <span className="mx-0.5 h-4 w-px bg-line" aria-hidden="true" />
          <SwatchPicker icon={Palette} title={t('editor.textColor')} colors={TEXT_COLORS} onPick={(color) => editor.chain().focus().setColor(color).run()} />
          <SwatchPicker icon={Highlighter} title={t('editor.highlight')} colors={HIGHLIGHT_COLORS} onPick={(color) => editor.chain().focus().toggleHighlight({ color }).run()} />
          <span className="mx-0.5 h-4 w-px bg-line" aria-hidden="true" />
          <ToolbarButton icon={List} title={t('editor.bulletList')} active={editor.isActive('bulletList')} disabled={disabled} onClick={() => editor.chain().focus().toggleBulletList().run()} />
          <ToolbarButton icon={ListOrdered} title={t('editor.numberedList')} active={editor.isActive('orderedList')} disabled={disabled} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
          <ToolbarButton icon={Quote} title={t('editor.blockquote')} active={editor.isActive('blockquote')} disabled={disabled} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
          <ToolbarButton icon={Code} title={t('editor.codeBlock')} active={editor.isActive('codeBlock')} disabled={disabled} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />
          <span className="mx-0.5 h-4 w-px bg-line" aria-hidden="true" />
          <ToolbarButton
            icon={LinkIcon}
            title={t('editor.insertLink')}
            active={editor.isActive('link')}
            disabled={disabled}
            onClick={() => openUrlDialog('link')}
          />
          <ToolbarButton
            icon={ImageIcon}
            title={t('editor.insertImage')}
            disabled={disabled}
            onClick={() => openUrlDialog('image')}
          />
          <ToolbarButton
            icon={TableIcon}
            title={t('editor.insertTable')}
            disabled={disabled}
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          />
          {mentionsEnabled && (
            <ToolbarButton
              icon={AtSign}
              title={t('editor.mention')}
              disabled={disabled}
              onClick={() => editor.chain().focus().insertContent('@').run()}
            />
          )}
          <span className="mx-0.5 h-4 w-px bg-line" aria-hidden="true" />
          <ToolbarButton icon={Undo2} title={t('editor.undo')} disabled={disabled} onClick={() => editor.chain().focus().undo().run()} />
          <ToolbarButton icon={Redo2} title={t('editor.redo')} disabled={disabled} onClick={() => editor.chain().focus().redo().run()} />
          {onAttach && (
            <button
              type="button"
              title={t('editor.attach')}
              aria-label={t('editor.attach')}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={onAttach}
              disabled={disabled}
              className="ml-auto rounded p-1.5 text-ink-muted hover:bg-line hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            >
              <Paperclip className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="min-w-0 overflow-x-auto" style={{ minHeight }}>
          <EditorContent editor={editor} />
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
      <Dialog
        open={urlDialogKind !== null}
        onOpenChange={(open) => {
          if (!open) closeUrlDialog();
        }}
      >
        <DialogContent initialFocusRef={urlInputRef}>
          <form onSubmit={submitUrl}>
            <DialogHeader>
              <DialogTitle>{urlDialogKind === 'image' ? t('editor.dialogImageTitle') : t('editor.dialogLinkTitle')}</DialogTitle>
              <DialogDescription>
                {urlDialogKind === 'image'
                  ? t('editor.imageHint')
                  : t('editor.linkHint')}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-5">
              <label htmlFor="rich-text-url" className="mb-1 block text-sm font-medium text-ink-muted">
                {t('editor.url')}
              </label>
              <input
                ref={urlInputRef}
                id="rich-text-url"
                type="text"
                inputMode="url"
                value={urlValue}
                onChange={(event) => {
                  setUrlValue(event.target.value);
                  if (urlError) setUrlError(null);
                }}
                aria-invalid={urlError ? 'true' : undefined}
                aria-describedby={urlError ? 'rich-text-url-error' : undefined}
                className="h-10 w-full rounded-md border border-line-strong bg-white px-3 text-sm text-ink outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                placeholder={urlDialogKind === 'image' ? 'https://example.com/image.png' : 'https://example.com'}
              />
              {urlError && <p id="rich-text-url-error" className="mt-1 text-xs text-danger-600" role="alert">{urlError}</p>}
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={closeUrlDialog}>{t('common.cancel')}</Button>
              <Button type="submit">{urlDialogKind === 'image' ? t('editor.insertImageAction') : t('editor.insertLinkAction')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
